import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { setAuthToken, clearAuthToken } from './client';

const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

export async function refreshToken(user) {
  if (!user) { clearAuthToken(); return null; }
  try {
    const token = await user.getIdToken(true);
    setAuthToken(token);
    return token;
  } catch (e) {
    console.warn('Token refresh failed:', e.message);
    return null;
  }
}

// ── Google Sign-In ────────────────────────────────────────────────────────────
export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  const result   = await signInWithPopup(auth, provider);
  await refreshToken(result.user);
  return result.user;
}

// ── Email / Password ──────────────────────────────────────────────────────────
export async function signInWithEmail(email, password) {
  const result = await signInWithEmailAndPassword(auth, email, password);
  await refreshToken(result.user);
  return result.user;
}

export async function signUpWithEmail(email, password, displayName) {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName) {
    await updateProfile(result.user, { displayName });
    await result.user.reload();
  }
  await refreshToken(result.user);
  return result.user;
}

export async function resetPassword(email) {
  await sendPasswordResetEmail(auth, email);
}

// ── Phone / OTP ───────────────────────────────────────────────────────────────
// Sets up invisible reCAPTCHA on a container element ID, returns verifier
export function setupRecaptcha(containerId) {
  if (window._recaptchaVerifier) {
    try { window._recaptchaVerifier.clear(); } catch (_) {}
  }
  window._recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: () => {},
  });
  return window._recaptchaVerifier;
}

export async function sendPhoneOtp(phoneNumber, recaptchaVerifier) {
  // phoneNumber must be in E.164 format: +919876543210
  const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
  return confirmation; // store this and call confirmation.confirm(otp) later
}

// ── Guest token (no anonymous auth — disabled in Firebase project) ─────────────
export function ensureGuestToken() {
  return new Promise((resolve) => {
    if (auth.currentUser) {
      refreshToken(auth.currentUser).catch(() => {}).finally(() => resolve());
      return;
    }
    const unsub = onAuthStateChanged(auth, async (user) => {
      unsub();
      if (user && !user.isAnonymous) {
        await refreshToken(user).catch(() => {});
      }
      resolve();
    });
  });
}

export async function signOut() {
  await firebaseSignOut(auth);
  clearAuthToken();
}

export function onAuthChange(callback) {
  return onAuthStateChanged(auth, async (user) => {
    if (user && !user.isAnonymous) {
      await refreshToken(user).catch(() => {});
    } else {
      clearAuthToken();
    }
    callback(user && !user.isAnonymous ? user : null);
  });
}

// Human-readable Firebase error messages
export function parseFirebaseError(code) {
  const map = {
    'auth/user-not-found':          'No account found with this email. Please sign up.',
    'auth/wrong-password':          'Incorrect password. Please try again.',
    'auth/invalid-credential':      'Incorrect email or password.',
    'auth/email-already-in-use':    'An account already exists with this email.',
    'auth/weak-password':           'Password must be at least 6 characters.',
    'auth/invalid-email':           'Please enter a valid email address.',
    'auth/too-many-requests':       'Too many attempts. Please try again later.',
    'auth/network-request-failed':  'Network error. Check your connection.',
    'auth/invalid-phone-number':    'Please enter a valid phone number with country code.',
    'auth/invalid-verification-code': 'Incorrect OTP. Please try again.',
    'auth/code-expired':            'OTP has expired. Please request a new one.',
    'auth/operation-not-allowed':   'This sign-in method is not enabled. Please use Google Sign-In.',
  };
  return map[code] || 'Something went wrong. Please try again.';
}
